// Theme code - receives messages and fires Kameleoon goals
(function () {
  // Debug mode
  const DEBUG = false;

  // Track processed goals to prevent duplicates on main window level
  const processedGoals = new Set();

  // Listen for messages from Customer Events
  window.addEventListener("message", function (event) {
    // Only process kameleoon goal messages
    if (!event.data || !event.data.type) return;

    // Handle goal messages
    if (event.data.type === "kameleoon_goal") {
      // Check if Kameleoon is available
      if (!window.Kameleoon?.API?.Goals) {
        if (DEBUG) console.log("[Kameleoon] API not ready, goal not fired:", event.data);
        return;
      }

      // Create unique key for this goal event
      const goalKey = `${event.data.goalName}_${
        event.data.productId || event.data.orderId || "unknown"
      }`;

      // Check if already processed (in case of duplicate messages)
      if (processedGoals.has(goalKey)) {
        if (DEBUG) console.log("[Kameleoon] Goal already processed:", goalKey);
        return;
      }

      // For AddtoCart, check sessionStorage as additional safety
      if (event.data.goalName === "AddtoCart" && event.data.productId) {
        const sessionKey = `atc_main_${event.data.productId}`;
        if (sessionStorage.getItem(sessionKey)) {
          if (DEBUG) console.log(
            "[Kameleoon] AddtoCart already fired for product:",
            event.data.productId
          );
          return;
        }
        sessionStorage.setItem(sessionKey, Date.now().toString());
      }

      // Mark as processed
      processedGoals.add(goalKey);

      // Fire the Kameleoon goal
      try {
        if (event.data.revenue > 0) {
          window.Kameleoon.API.Goals.processConversion(
            event.data.goalId,
            event.data.revenue
          );
          if (DEBUG) console.log(
            "[Kameleoon] Goal fired:",
            event.data.goalName,
            "ID:",
            event.data.goalId,
            "Revenue:",
            event.data.revenue
          );
        } else {
          window.Kameleoon.API.Goals.processConversion(event.data.goalId);
          if (DEBUG) console.log(
            "[Kameleoon] Goal fired:",
            event.data.goalName,
            "ID:",
            event.data.goalId
          );
        }
      } catch (error) {
        if (DEBUG) console.error("[Kameleoon] Error firing goal:", error);
      }
    }

    // Handle clear ATC message
    if (event.data.type === "kameleoon_clear_atc") {
      if (DEBUG) console.log("[Kameleoon] Clearing ATC tracking after purchase");

      // Clear all ATC entries from sessionStorage
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith("atc_main_") || key.startsWith("atc_"))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      // Clear memory tracking
      processedGoals.clear();

      if (DEBUG) console.log(
        "[Kameleoon] Cleared",
        keysToRemove.length,
        "ATC tracking entries"
      );
    }
  });

  // Debug: Log Kameleoon availability on load
  window.addEventListener("load", function () {
    if (DEBUG) {
      if (window.Kameleoon?.API?.Goals) {
        console.log("[Kameleoon] API ready on page load");
      } else {
        console.log("[Kameleoon] API not available on page load");
      }
    }
  });
})();

//////////////////////////////////////////////////////
// Kameleoon Cross-Domain Tracking with Pandectes GDPR
(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    debugMode: false, // Set to true for debugging
    kameleoonParamName: "kam_visitor",
  };

  // Logging helper
  function log(message, data) {
    if (CONFIG.debugMode) {
      console.log(`[Kameleoon GDPR] ${message}`, data || "");
    }
  }

  // Initialize Kameleoon queue if not exists
  window.kameleoonQueue = window.kameleoonQueue || [];

  // Track last applied state to prevent duplicate API calls
  let lastAppliedState = undefined;

  // Step 1: Read and set visitor code from URL (BEFORE consent check)
  function setVisitorCodeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const visitorCodeFromURL = urlParams.get(CONFIG.kameleoonParamName);

    if (visitorCodeFromURL) {
      log("Found visitor code in URL", visitorCodeFromURL);

      // Set visitor code immediately using command queue
      window.kameleoonQueue.push({
        level: "IMMEDIATE",
        command: function () {
          try {
            Kameleoon.API.Visitor.setVisitorCode(visitorCodeFromURL);
            log("Visitor code set successfully", visitorCodeFromURL);

            // Clean URL after setting the code
            cleanURLParameter();
          } catch (error) {
            log("Error setting visitor code", error);
          }
        },
      });
    } else {
      log("No visitor code found in URL");
    }
  }

  // Clean URL parameter after use
  function cleanURLParameter() {
    const url = new URL(window.location);
    url.searchParams.delete(CONFIG.kameleoonParamName);

    if (url.searchParams.toString() === "") {
      window.history.replaceState({}, document.title, url.pathname);
    } else {
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  // Check current Pandectes consent from cookie
  // Returns: number (preferences), or null if no cookie/no decision
  function checkCurrentConsent() {
    try {
      // Look for pandectes_gdpr cookie
      const cookieMatch = document.cookie.match(/pandectes_gdpr=([^;]+)/);

      if (!cookieMatch || !cookieMatch[1]) {
        log("No Pandectes cookie found - consent not given yet");
        return null; // No cookie = no decision made yet
      }

      // Decode base64 and parse JSON
      const decodedValue = atob(cookieMatch[1]);
      const consentData = JSON.parse(decodedValue);

      log("Current Pandectes consent data", consentData);

      // Check if this is a real user decision or just default state
      // Pandectes sets consentType to help identify this
      if (
        consentData.consentType === "default" ||
        consentData.status === "default"
      ) {
        log("Pandectes in default state - no user decision yet");
        return null;
      }

      // Check preferences value
      if (typeof consentData.preferences === "number") {
        return consentData.preferences;
      } else if (consentData.status === "allow") {
        // If status is allow but no preferences, user accepted all
        return 0;
      } else if (consentData.status === "deny") {
        // User denied all
        return 7;
      } else {
        // Unknown state - treat as no decision
        log("Unknown Pandectes state - treating as no decision");
        return null;
      }
    } catch (error) {
      log("Error parsing Pandectes cookie", error);
      return null; // Error = no valid decision
    }
  }

  // Apply consent to Kameleoon based on Pandectes preferences
  function applyKameleoonConsent(preferences, source) {
    // Skip if state hasn't changed
    if (lastAppliedState === preferences) {
      log(`No change needed (${source}):`, preferences);
      return;
    }

    // Determine consent state text for logging
    const stateText =
      preferences === null
        ? "UNKNOWN (no decision)"
        : preferences === 0
        ? "ALL ALLOWED"
        : preferences === 7
        ? "ALL DENIED"
        : `PARTIAL (preferences: ${preferences})`;

    if (CONFIG.debugMode) {
      console.log(`[Kameleoon] Consent state: ${stateText} (source: ${source})`);
    }

    // Only apply consent if we have a definitive decision (not null)
    if (preferences !== null) {
      window.kameleoonQueue.push({
        level: "NORMAL",
        command: function () {
          try {
            // Check if Performance (2) or Targeting (4) cookies are allowed
            // preferences === 0 means all cookies allowed
            // (preferences & 2) === 0 means Performance allowed
            // (preferences & 4) === 0 means Targeting allowed

            const performanceAllowed = (preferences & 2) === 0;
            const targetingAllowed = (preferences & 4) === 0;
            const hasConsent =
              preferences === 0 || performanceAllowed || targetingAllowed;

            log("Pandectes preferences", {
              preferences: preferences,
              performanceAllowed: performanceAllowed,
              targetingAllowed: targetingAllowed,
              hasConsent: hasConsent,
            });

            if (hasConsent) {
              Kameleoon.API.Core.enableLegalConsent("BOTH");
              log("Kameleoon tracking ENABLED");
            } else {
              Kameleoon.API.Core.disableLegalConsent("BOTH");
              log("Kameleoon tracking DISABLED");
            }

            lastAppliedState = preferences;
          } catch (error) {
            log("Error applying Kameleoon consent", error);
          }
        },
      });
    } else {
      // Null state - Kameleoon will handle this automatically
      // Don't call enable or disable, let Kameleoon stay in null state
      lastAppliedState = null;
      log("Null state - Kameleoon remains in unknown state (no tracking)");
    }
  }

  // Listen for Pandectes consent events
  function listenForConsentChanges() {
    // Primary Pandectes event listener
    window.addEventListener("PandectesEvent_OnConsent", function (event) {
      log("Pandectes consent event received", event.detail);

      if (event.detail) {
        // Check if this is a real user decision
        if (event.detail.consentType === "default") {
          log("Default consent event - ignoring (no user decision)");
          return;
        }

        if (typeof event.detail.preferences === "number") {
          applyKameleoonConsent(
            event.detail.preferences,
            "PandectesEvent_OnConsent"
          );
          log("Consent type", event.detail.consentType);
        }
      }
    });

    // Also listen for consent updated events
    window.addEventListener("PandectesEvent_ConsentUpdated", function (event) {
      log("Pandectes consent updated event", event.detail);
      const currentPreferences = checkCurrentConsent();
      applyKameleoonConsent(
        currentPreferences,
        "PandectesEvent_ConsentUpdated"
      );
    });

    // No polling - only event-driven updates
    log("Event listeners registered");
  }

  // Initialize everything
  function initialize() {
    if (CONFIG.debugMode) {
      console.log("[Kameleoon GDPR] Initializing Pandectes integration");
    }

    // Step 1: Set visitor code from URL immediately
    setVisitorCodeFromURL();

    // Step 2: Check current consent and apply
    // IMPORTANT: This might be null if user hasn't decided
    const currentPreferences = checkCurrentConsent();

    // Only apply if there's an actual decision
    if (currentPreferences !== null) {
      applyKameleoonConsent(currentPreferences, "initialization");
    } else {
      if (CONFIG.debugMode) {
        console.log(
          "[Kameleoon GDPR] No user consent decision yet - Kameleoon remains in null state"
        );
      }
      lastAppliedState = null;
    }

    // Step 3: Listen for consent changes (event-driven, no polling)
    listenForConsentChanges();

    // Debug info
    log("Debug info:", {
      currentPreferences: currentPreferences,
      cookieExists: document.cookie.includes("pandectes_gdpr"),
      kameleoonDefined: typeof Kameleoon !== "undefined",
    });
  }

  // Start initialization
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }
})();
